import {
  Create,
  TextInput,
  NumberInput,
  SelectInput,
  required,
  useNotify,
  RadioButtonGroupInput,
  DateInput,
  SaveButton,
  useSaveContext
} from "react-admin";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
  Grid,
  Button
} from "@mui/material";
import ComponentEdit from "../../components/PageComponents/ComponentEdit";
import FormField from "../../components/Common/FormField";
import { useCurrentProject } from "../../contexts/AccountProjectContext";
import { useSyncProjectIdToForm } from "../../hooks/useSyncProjectIdToForm";
import { WizardForm } from '@react-admin/ra-form-layout';
import { useFormContext } from "react-hook-form";
import { getData } from 'country-list';
import { useWizardFormContext } from '@react-admin/ra-form-layout';
import React from "react";
import { paymentCurrencyChoices } from "../../models/paymentCurrency";

const BasicInformationStep = () => {
  const { t }: { t: any } = useTranslation();
  const { formState: { errors }, setValue } = useFormContext();
  
  React.useEffect(() => {
    setValue('type', 'cryptoOrder');
  }, [setValue]);
  
  return (
    <>
      <TextInput
        source="type"
        defaultValue="cryptoOrder"
        style={{ display: 'none' }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormField label={t("Internal reference")}>
            <TextInput
              source="internalRef"
              label={t("Internal reference")}
              fullWidth
              validate={required()}
              helperText={t("A unique identifier for this payment link")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Amount (EUR)")}>
            <NumberInput
              source="euroAmount"
              label={t("Amount (EUR)")}
              fullWidth
              validate={required()}
              helperText={t("The amount to be paid in euros")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Payment currency")}>
            <SelectInput
              source="currency"
              label={t("Payment currency")}
              fullWidth
              validate={required()}
              choices={[
                ...paymentCurrencyChoices,
                { id: "customer_choice", name: "Let customer choose" },
              ]}
              helperText={t("Select the cryptocurrency for payment")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Expiration date")}>
            <DateInput
              source="expiresAt"
              label={t("Expiration date")}
              fullWidth
              validate={required()}
              helperText={t("The payment link will expire on this date")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Customer name")}>
            <TextInput
              source="customerName"
              label={t("Customer name")}
              fullWidth
              validate={required()}
              helperText={t("Name of the customer who will receive the payment link")}
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label={t("Customer email")}>
            <TextInput
              source="customerEmail"
              label={t("Customer email")}
              fullWidth
              validate={required()}
              type="email"
              helperText={t("Email address where the payment link will be sent")}
            />
          </FormField>
        </Grid>
      </Grid>
    </>
  );
};

const SendLinkStep = () => {
  const { t }: { t: any } = useTranslation();
  const { watch, formState: { errors } } = useFormContext();
  const errorMessageProject = Boolean(errors?.projectId);
  const sendLink = watch("sendLink");

  const conditionalValidate = (value: any) => {
    const formValues = watch();
    if (formValues.sendLink === "yes" && !value) {
      return "This field is required when sending link to customer";
    }
    return undefined;
  };

  return (
    <>

      <Box mb={3}>
        <FormField label={t("Send the payment link to the customer by email?")}>
          <RadioButtonGroupInput
            source="sendLink"
            label={t("Send the payment link to the customer by email?")}
            choices={[
              { id: "yes", name: t("Yes") },
              { id: "no", name: t("No") },
            ]}
            validate={required()}
          />
        </FormField>
      </Box>

      {sendLink === "yes" && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormField label={t("Sender name")}>
                <TextInput
                  source="senderName"
                  label={t("Sender name")}
                  fullWidth
                  validate={conditionalValidate}
                  helperText={t("Your name as it will appear in the email")}
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormField label={t("Sender company name")}>
                <TextInput
                  source="senderCompanyName"
                  label={t("Sender company name")}
                  fullWidth
                  validate={conditionalValidate}
                  helperText={t("Your company name as it will appear in the email")}
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormField label={t("Sender email")}>
                <TextInput
                  source="senderEmail"
                  label={t("Sender email")}
                  fullWidth
                  validate={conditionalValidate}
                  type="email"
                  helperText={t("Your email address as it will appear in the email")}
                />
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <FormField label={t("Message")}>
                <TextInput
                  source="message"
                  label={t("Message")}
                  fullWidth
                  multiline
                  rows={4}
                  validate={conditionalValidate}
                  helperText={t("The message that will be included in the email")}
                />
              </FormField>
            </Grid>
          </Grid>

          <TextInput
            source="sendMessage"
            defaultValue={true}
            style={{ display: 'none' }}
          />
      {errorMessageProject && (

        <Grid item xs={12} mt={4}>
          <Alert severity="error">
            Entity selection is not configured to create a crypto payment.
            Please select another entity on top of the page or update the
            configuration in the entity settings.
          </Alert>
        </Grid>
      )}
        </>
      )}
    </>
  );
};

const CustomToolbar = () => {
  const { t }: { t: any } = useTranslation();
  const { hasNextStep, hasPreviousStep, goToNextStep, goToPreviousStep } = useWizardFormContext();
  const [validationErrors] = React.useState<string[]>([]);

  const handleNext = () => {
    goToNextStep();
  };

  const handlePrevious = () => {
    goToPreviousStep();
  };

  return (
    <>
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ m: 2 }}>
          Please correct the following errors:
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
      <Box display="flex" justifyContent="flex-end" gap={1} m={2}>
        {hasPreviousStep && (
          <Button
            onClick={handlePrevious}
            variant="outlined"
          >
            {t("Back")}
          </Button>
        )}
        {hasNextStep ? (
          <Button
            onClick={handleNext}
            variant="contained"
            color="primary"
          >
            {t("Next")}
          </Button>
        ) : (
          <SaveButton
            label={t("Create payment link")}
            variant="contained"
            color="primary"
          />
        )}
      </Box>
    </>
  );
};

export const PaymentLinkCreate = () => {
  const { t }: { t: any } = useTranslation();
  const { typeProject } = useCurrentProject();
  const projectId = useSyncProjectIdToForm();

  const validateCryptoOrder = (values: any) => {
    const errors: any = {};
    
    if (typeProject !== "cryptoPayment") {
      errors.projectId =
        "Entity selection is not configured to create a crypto payment. Please select another entity on top of the page or update the configuration in the entity settings.";
    }
    if( !values.internalRef || values.internalRef.trim() === '') {
      errors.internalRef = "Internal Reference is required";
    }
    if( !values.euroAmount || values.euroAmount < 30) {
      errors.euroAmount = "Amount is required and must be equal or greater than 30";
    }
    if( !values.currency || values.currency.trim() === '') {  
      errors.currency = "Payment Currency is required";
    }
    if( !values.expiresAt) {
      errors.expiresAt = "Expiration Date is required";
    }
    if( !values.customerName || values.customerName.trim() === '') {  
      errors.customerName = "Customer Name is required";
    }
    if( !values.customerEmail || values.customerEmail.trim() === '') {  
      errors.customerEmail = "Customer Email is required";
    }
    
    if (values.sendLink === "yes") {
      if (!values.senderName) {
        errors.senderName = "Sender name is required when sending link to customer";
      }
      if (!values.senderCompanyName) {
        errors.senderCompanyName = "Sender company name is required when sending link to customer";
      }
      if (!values.senderEmail) {
        errors.senderEmail = "Sender email is required when sending link to customer";
      }
      if (!values.message) {
        errors.message = "Message is required when sending link to customer";
      }
    }

    return errors;
  };

  const transformData = async (data: any) => {
    return {
      ...data,
      projectId,
    };
  };

  return (
    <ComponentEdit
      title="New payment link"
      subtitle="Create a payment link to send to a customer."
    >
      <Card>
        <Box
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">
              {t("Payment link details")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("Set the amount, expiry and customer, then choose whether to email the link.")}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <CardContent
          sx={{
            p: 4,
          }}
        >
          <Create redirect="show" transform={transformData}>
            <Box
              sx={{
                "& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": { color: "text.primary" },
              }}
            >
            <WizardForm 
              validate={validateCryptoOrder}
              toolbar={<CustomToolbar />}
            >
              <WizardForm.Step label={t("Basic information")}>
                <BasicInformationStep />
              </WizardForm.Step>
              <WizardForm.Step label={t("Send link")}>
                <SendLinkStep />
              </WizardForm.Step>
            </WizardForm>
            </Box>
          </Create>
        </CardContent>
      </Card>
    </ComponentEdit>
  );
};

export default PaymentLinkCreate;
